/**
 * useEgoNetwork - Fetches and transforms network graph data into ego network.
 *
 * 1. Calls api.getNetworkGraphData(entityId) for topology
 * 2. Extracts 1.5-hop ego network
 * 3. Resolves node labels from graph data
 * 4. Assigns zones using proxy metrics
 * 5. Computes network statistics
 */

import { useState, useEffect, useCallback} from 'react';
import api from '../../../services/api';
import {
  extractEgoNetwork,
  resolveNodeLabels,
  assignZone,
  computeNetworkStats,
  getNodeDegree,
} from '../utils/networkUtils';

function useEgoNetwork(entityId, dateRange) {
  const [state, setState] = useState({
    nodes: [],
    edges: [],
    egoId: null,
    networkStats: null,
    loading: false,
    error: null,
  });

  const fetchData = useCallback(async () => {
    if (!entityId) {
      setState({
        nodes: [],
        edges: [],
        egoId: null,
        networkStats: null,
        loading: false,
        error: null,
      });
      return;
    }

    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      // Fetch network graph from Token Examiner API
      const graphData = await api.getNetworkGraphData(entityId);

      if (!graphData?.nodes?.length) {
        setState({
          nodes: [],
          edges: [],
          egoId: null,
          networkStats: null,
          loading: false,
          error: null,
        });
        return;
      }

      // Extract 1.5-hop ego network
      const egoNetwork = extractEgoNetwork(graphData);

      // Resolve labels (cross-reference with rollup names)
      // The network-graph endpoint may return nodes with label or name fields
      const labeledNodes = resolveNodeLabels(egoNetwork.nodes, graphData.nodes);

      // Assign zones and compute degree for each node
      const enrichedNodes = labeledNodes.map((node) => ({
        ...node,
        zone: assignZone({
          mention_count: node.mention_count || node.mentions || 0,
          evidence_count: node.evidence_count || node.weight || 0,
          last_seen: node.last_seen || node.lastSeen,
        }),
        degree: getNodeDegree(node.id, egoNetwork.edges),
      }));

      // Compute network stats
      const stats = computeNetworkStats(enrichedNodes, egoNetwork.edges);

      setState({
        nodes: enrichedNodes,
        edges: egoNetwork.edges,
        egoId: egoNetwork.egoId,
        networkStats: stats,
        loading: false,
        error: null,
      });
    } catch (err) {
      console.error('useEgoNetwork fetch error:', err);
      setState((prev) => ({
        ...prev,
        loading: false,
        error: err,
      }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId, dateRange]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const retry = useCallback(() => {
    fetchData();
  }, [fetchData]);

  return {
    ...state,
    retry,
  };
}

export default useEgoNetwork;
